/**
 * Client Form Component
 * Create and edit client with multi-section form
 */

import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save, X } from 'lucide-react'

import { useClient, useCreateClient, useUpdateClient } from '@/hooks'
import { toast } from '@/lib/toast'
import { Button } from '@/components/ui/button'
import { FormSkeleton } from '@/components/loading'
import { ClientFormFields } from '@/components/forms/ClientFormFields'
import { clientFormSchema, clientFormDefaultValues, processClientFormData, ClientFormData } from '@/lib/schemas/client-form'
import { ClientCreate } from '@/types/client'

export function ClientForm() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id

  // Fetch existing client if editing
  const { data: client, isLoading: isLoadingClient } = useClient(id!, {
    enabled: isEditing,
  })

  // Mutations
  const createClient = useCreateClient()
  const updateClient = useUpdateClient()

  // Form setup
  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: clientFormDefaultValues,
  })

  const { handleSubmit, formState: { isSubmitting }, setValue } = form

  // Load existing client data
  useEffect(() => {
    if (client) {
      setValue('firstname', client.firstname || '')
      setValue('middlename', client.middlename || '')
      setValue('lastname', client.lastname || '')
      setValue('email', client.email || '')
      setValue('gender', client.gender as ClientFormData['gender'])
      setValue('birthday', client.birthday || '')
      setValue('monthly_income', client.monthly_income || 0)
      setValue('civil_status', client.civil_status || '')
      setValue('religion', client.religion || '')
      setValue('citizenship', client.citizenship || 'Filipino')
      setValue('educational_attainment', client.educational_attainment || '')
      setValue('indigenous_group', client.indigenous_group || '')
      setValue('street_name', client.street_name || '')
      setValue('barangay', client.barangay || '')
      setValue('city', client.city || '')
      setValue('province', client.province || '')
      setValue('postal_code', client.postal_code || '')
      setValue('contact_number', client.contact_number || '')
    }
  }, [client, setValue])

  // Submit handler
  const onSubmit = async (data: ClientFormData) => {
    try {
      const cleanData = processClientFormData(data)

      if (isEditing) {
        await updateClient.mutateAsync({ id: id!, data: cleanData })
        toast.success('Client updated successfully!')
      } else {
        await createClient.mutateAsync(cleanData as unknown as ClientCreate)
        toast.success('Client created successfully!')
      }
      navigate('/clients')
    } catch (error: any) {
      toast.error(
        isEditing ? 'Failed to update client' : 'Failed to create client',
        error.response?.data?.detail || error.message
      )
    }
  }

  if (isLoadingClient) {
    return <FormSkeleton fields={15} />
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-primary">
          {isEditing ? 'Edit Client' : 'New Client'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isEditing
            ? 'Update client information below'
            : 'Fill in the form to create a new client'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <ClientFormFields
          form={form}
          initialProvinceName={client?.province}
          initialCityName={client?.city}
        />

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/clients')}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary hover:bg-primary/90"
          >
            <Save className="mr-2 h-4 w-4" />
            {isSubmitting ? 'Saving...' : isEditing ? 'Update Client' : 'Create Client'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default ClientForm
